import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Chip,
  ListSubheader,
  Skeleton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SmartToyRounded from '@mui/icons-material/SmartToyRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import {
  fetchTenantSettings,
  saveTenantSettings,
  getTenantIdFromStorage,
} from '../../services/tenantSettingsService';
import {
  fetchModelCatalog,
  type ChatModelOption,
  type EmbeddingModelOption,
  type ModelCatalog,
} from '../../services/modelCatalogService';
import SettingsPageHeader from './components/SettingsPageHeader';
import StickySaveBar from './components/StickySaveBar';

// All model-specific behavior (provider, base URL, temperature,
// reasoning_effort, max tokens, which OpenAI endpoint to hit) is locked in
// the backend registry. The UI only collects what the backend can't infer:
// the model id and the API key. We deliberately don't expose a base-URL
// override — every time we did, a stale value from a prior provider leaked
// into the new one and produced 404s. If a customer ever needs a
// self-hosted/proxy endpoint we'll add it as a provider-specific field.

const PROVIDER_LABEL: Record<ChatModelOption['provider'], string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
  'azure-openai': 'Azure OpenAI',
};

const PROVIDER_KEY_PLACEHOLDER: Record<ChatModelOption['provider'], string> = {
  openai: 'sk-…',
  anthropic: 'sk-ant-…',
  google: 'AIza… (Google Generative AI key)',
  'azure-openai': 'az-…',
};

const isMaskedKey = (v: string) => typeof v === 'string' && v.includes('••••••••');

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  trailing?: React.ReactNode;
}

function SectionHeader({ icon, title, subtitle, trailing }: SectionHeaderProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: subtitle ? 2 : 2.5, flexWrap: 'wrap' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={(t) => ({
            width: 32,
            height: 32,
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.brand.primarySoft,
            color: t.tokens.brand.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          })}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1rem', lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.45, mt: 0.25 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {trailing}
    </Box>
  );
}

// Group chat models by provider so the Select renders nicely with subheaders.
function groupByProvider<T extends { provider: ChatModelOption['provider'] }>(
  models: T[],
): Array<{ provider: ChatModelOption['provider']; items: T[] }> {
  const order: ChatModelOption['provider'][] = ['openai', 'anthropic', 'google', 'azure-openai'];
  const groups = new Map<ChatModelOption['provider'], T[]>();
  for (const m of models) {
    if (!groups.has(m.provider)) groups.set(m.provider, []);
    groups.get(m.provider)!.push(m);
  }
  return order
    .filter((p) => groups.has(p))
    .map((p) => ({ provider: p, items: groups.get(p)! }));
}

export default function AISettingsPage() {
  const { notify } = React.useContext(SnackbarContext);
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);

  const [catalog, setCatalog] = React.useState<ModelCatalog | null>(null);
  const [chatModelId, setChatModelId] = React.useState<string>('');
  const [embeddingModelId, setEmbeddingModelId] = React.useState<string>('');

  // Per-provider keys, kept separate so switching models across providers
  // preserves what the user already typed.
  const [keysByProvider, setKeysByProvider] = React.useState<Record<string, string>>({});
  const [embeddingApiKey, setEmbeddingApiKey] = React.useState<string>('');

  const [showKey, setShowKey] = React.useState<Record<string, boolean>>({});
  const [loading, setLoading] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);

  const selectedChatModel: ChatModelOption | undefined = React.useMemo(
    () => catalog?.chat_models.find((m) => m.id === chatModelId),
    [catalog, chatModelId],
  );
  const selectedEmbeddingModel: EmbeddingModelOption | undefined = React.useMemo(
    () => catalog?.embedding_models.find((m) => m.id === embeddingModelId),
    [catalog, embeddingModelId],
  );
  const activeProvider = selectedChatModel?.provider;
  const activeKey = activeProvider ? keysByProvider[activeProvider] || '' : '';
  const setActiveKey = (v: string) => {
    if (!activeProvider) return;
    setKeysByProvider((m) => ({ ...m, [activeProvider]: v }));
  };

  const hydrate = React.useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [cat, s] = await Promise.all([
        fetchModelCatalog(tenantId),
        fetchTenantSettings(tenantId),
      ]);
      setCatalog(cat);

      // Resolve the chat model: prefer what the tenant has saved if it's
      // still in the catalog; otherwise drop to the catalog default.
      const savedModel = typeof s['LLM_MODEL'] === 'string' ? s['LLM_MODEL'] : '';
      const chatExists = cat.chat_models.some((m) => m.id === savedModel);
      const nextChatId = chatExists ? savedModel : cat.defaults.chat_model_id;
      setChatModelId(nextChatId);

      const savedEmbed = typeof s['EMBEDDING_MODEL'] === 'string' ? s['EMBEDDING_MODEL'] : '';
      const embedExists = cat.embedding_models.some((m) => m.id === savedEmbed);
      setEmbeddingModelId(embedExists ? savedEmbed : cat.defaults.embedding_model_id);

      const nextChat = cat.chat_models.find((m) => m.id === nextChatId);
      const savedKey = typeof s['LLM_API_KEY'] === 'string' ? s['LLM_API_KEY'] : '';
      if (nextChat && savedKey) {
        setKeysByProvider((m) => ({ ...m, [nextChat.provider]: savedKey }));
      }

      if (typeof s['EMBEDDING_API_KEY'] === 'string') setEmbeddingApiKey(s['EMBEDDING_API_KEY']);
    } catch (e: any) {
      notify(e?.message || 'Failed to load AI configuration', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, notify]);

  React.useEffect(() => {
    hydrate();
  }, [hydrate]);

  const save = async () => {
    if (!tenantId) {
      notify('Missing tenant', 'error');
      return;
    }
    if (!selectedChatModel) {
      notify('Pick a chat model first', 'error');
      return;
    }
    setSaving(true);
    try {
      await saveTenantSettings(tenantId, {
        // The model id is the only chat-side knob — everything else is
        // resolved from the backend registry at call time.
        LLM_MODEL: selectedChatModel.id,
        // Provider is derivable from model, but we keep it written for
        // any legacy code paths still reading LLM_PROVIDER.
        LLM_PROVIDER: selectedChatModel.provider,
        LLM_API_KEY: activeKey,
        EMBEDDING_MODEL: selectedEmbeddingModel?.id || embeddingModelId,
        EMBEDDING_PROVIDER: selectedEmbeddingModel?.provider || 'openai',
        EMBEDDING_API_KEY: embeddingApiKey,
      });
      notify('AI configuration saved', 'success');
    } catch (e: any) {
      notify(e?.message || 'Failed to save AI configuration', 'error');
    } finally {
      setSaving(false);
    }
  };

  const cardSx = { mb: 3 };
  const cardContentSx = {
    px: { xs: 2, sm: 3 },
    py: { xs: 2, sm: 3 },
    '&:last-child': { pb: { xs: 2, sm: 3 } },
  };

  const chatGroups = catalog ? groupByProvider(catalog.chat_models) : [];

  return (
    <Box sx={{ width: '100%' }}>
      <SettingsPageHeader description="Pick a model and add your API key. Everything else is tuned for you." />

      {/* Model + Key */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<SmartToyRounded sx={{ fontSize: 18 }} />}
            title="Chat model"
            subtitle="The LLM that powers Copilot. Each option is preconfigured with the right reasoning effort, temperature, and token limits for that model."
            trailing={
              activeProvider ? (
                <Chip
                  size="small"
                  label={PROVIDER_LABEL[activeProvider]}
                  sx={(t) => ({
                    bgcolor: t.tokens.brand.primarySoft,
                    color: t.tokens.brand.primary,
                    fontWeight: 600,
                    border: 'none',
                  })}
                />
              ) : undefined
            }
          />

          {loading && !catalog ? (
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="chat-model-label">Model</InputLabel>
              <Select
                labelId="chat-model-label"
                label="Model"
                value={chatModelId}
                onChange={(e) => setChatModelId(String(e.target.value))}
              >
                {chatGroups.flatMap((group) => [
                  <ListSubheader key={`hdr-${group.provider}`}>
                    {PROVIDER_LABEL[group.provider]}
                  </ListSubheader>,
                  ...group.items.map((m) => (
                    <MenuItem key={m.id} value={m.id} sx={{ display: 'block', py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                          {m.label}
                        </Typography>
                        {m.is_reasoning_model ? (
                          <Chip size="small" label="Reasoning" sx={{ height: 18, fontSize: 10 }} />
                        ) : null}
                        {m.is_default ? (
                          <Chip
                            size="small"
                            label="Recommended"
                            color="primary"
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        ) : null}
                      </Stack>
                      {m.description ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {m.description}
                        </Typography>
                      ) : null}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="API Key"
            type={activeProvider && showKey[activeProvider] ? 'text' : 'password'}
            value={activeKey}
            onChange={(e) => setActiveKey(e.target.value)}
            onFocus={() => {
              if (isMaskedKey(activeKey)) setActiveKey('');
            }}
            placeholder={activeProvider ? PROVIDER_KEY_PLACEHOLDER[activeProvider] : ''}
            disabled={!activeProvider}
            helperText={
              isMaskedKey(activeKey)
                ? 'A key is already saved. Clear the field to enter a new one.'
                : 'Encrypted at rest and never leaves your tenant.'
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
                </InputAdornment>
              ),
              endAdornment: activeProvider ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() =>
                      setShowKey((s) => ({ ...s, [activeProvider]: !s[activeProvider] }))
                    }
                    edge="end"
                    aria-label="toggle key visibility"
                  >
                    {showKey[activeProvider] ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </CardContent>
      </Card>

      {/* Embeddings */}
      <Card sx={cardSx}>
        <CardContent sx={cardContentSx}>
          <SectionHeader
            icon={<StorageRounded sx={{ fontSize: 18 }} />}
            title="Embeddings"
            subtitle="Vector model used to index your schema and knowledge base for semantic search."
          />

          {loading && !catalog ? (
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
          ) : (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="embedding-model-label">Embedding model</InputLabel>
              <Select
                labelId="embedding-model-label"
                label="Embedding model"
                value={embeddingModelId}
                onChange={(e) => setEmbeddingModelId(String(e.target.value))}
              >
                {(catalog?.embedding_models || []).map((m) => (
                  <MenuItem key={m.id} value={m.id} sx={{ display: 'block', py: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {m.label}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${m.dimensions} dims`}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                      {m.is_default ? (
                        <Chip
                          size="small"
                          label="Recommended"
                          color="primary"
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      ) : null}
                    </Stack>
                    {m.description ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        {m.description}
                      </Typography>
                    ) : null}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Embeddings API Key"
            type={showKey.embedding ? 'text' : 'password'}
            value={embeddingApiKey}
            onChange={(e) => setEmbeddingApiKey(e.target.value)}
            onFocus={() => {
              if (isMaskedKey(embeddingApiKey)) setEmbeddingApiKey('');
            }}
            placeholder="sk-…"
            helperText={
              isMaskedKey(embeddingApiKey)
                ? 'A key is already saved. Clear the field to enter a new one.'
                : 'Used for embedding requests. Can differ from the main LLM key.'
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyRounded sx={(t) => ({ fontSize: 16, color: t.tokens.text.tertiary })} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowKey((s) => ({ ...s, embedding: !s.embedding }))}
                    edge="end"
                    aria-label="toggle key visibility"
                  >
                    {showKey.embedding ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      <StickySaveBar
        description="Changes apply on the next Copilot request — no restart required."
        onSave={save}
        onReset={hydrate}
        saving={saving}
        disabled={loading || !catalog}
      />
    </Box>
  );
}
